window.Alpine.data('searchAutocomplete', searchAutocomplete);

export default function searchAutocomplete(config = {}) {
	return {
		// Props passed from Blade
		resultsOpen: config.resultsOpen ?? false,
		isMobileMenuSearch: config.isMobileMenuSearch ?? false,

		// Search state
		query: '',
		queryId: null,
		minQueryLength: 2,
		showResults: false,
		results: [],
		resultKeys: [],
		highlightedIndex: -1,
		resultItemActive: null,
		loading: false,

		// UI state
		expanded: false,
		suppressWatcher: false,

		init() {
			this.query = this.$wire.get('query');

			// Update Livewire component when query changes
			this.$watch('query', this.handleQueryChange.bind(this));

			// Preload results if query is already populated
			if (this.query.length >= this.minQueryLength) {
				this.fetchResults();
				this.showResults = true;
			}
		},

		handleQueryChange: Alpine.debounce(function (value, oldValue) {
			if (this.suppressWatcher || value === oldValue) return;

			this.$wire.set('query', value);

			if (value.length < this.minQueryLength) {
				this.results = [];
				this.resultKeys = [];
				this.showResults = false;
				this.expanded = true;
				return;
			}

			this.fetchResults();
		}, 150),

		// Input
		onInput() {
			this.highlightedIndex = -1;
		},

		onFocus() {
			this.setExpandedAndShowResults();
		},

		onClickResult(result) {
			this.suppressWatcher = true;
			this.query = result.name;

			// Set index & active for keyboard/mouse UI
			this.highlightedIndex = this.resultKeys.indexOf(result.id);
			this.resultItemActive = result.id;

			this.submit();
		},

		closeResults() {
			this.showResults = false;
			this.expanded = false;

			// Blur input if it exists
			if (this.$refs.searchInput) {
				this.$refs.searchInput.blur();
			}
		},

		fetchResults() {
			this.loading = true;

			const currentQueryId = Date.now();
			this.queryId = currentQueryId;

			this.$wire
				.call('fetchResults')
				.then((results) => {
					if (this.queryId !== currentQueryId) return; // Ignore stale responses

					// console.log('Results fetched:', results); // Debugging
					this.results = results;

					// Reset highlighted/active item and scoll position on results update
					this.$nextTick(() => {
						this.highlightedIndex = -1;
						this.resultItemActive = null;

						const list = this.$refs.resultsList;
						if (list) list.scrollTop = 0;
					});

					this.resultKeys = results.map((r) => r.id);
					this.loading = false;

					this.setExpandedAndShowResults();
				})
				.catch((error) => {
					if (this.queryId !== currentQueryId) return; // Ignore stale responses

					console.error('Error fetching results:', error);

					this.loading = false;
					this.showResults = false;
				});
		},

		highlightMatch(text) {
			if (!this.query) return text;

			const query = this.query.trim();

			if (!query) return text;

			const regex = new RegExp(`(${query})`, 'ig');

			return text.replace(regex, '<span class="font-bold text-purple-950">$1</span>');
		},

		// Keyboard navigation
		selectNext() {
			if (this.resultKeys.length === 0) return;

			this.highlightedIndex = this.highlightedIndex < this.resultKeys.length - 1 ? this.highlightedIndex + 1 : 0;

			this.resultItemActive = this.resultKeys[this.highlightedIndex];
			this.scrollToHighlighted();
		},

		selectPrevious() {
			if (this.resultKeys.length === 0) return;

			this.highlightedIndex = this.highlightedIndex > 0 ? this.highlightedIndex - 1 : this.resultKeys.length - 1;

			this.resultItemActive = this.resultKeys[this.highlightedIndex];
			this.scrollToHighlighted();
		},

		scrollToHighlighted() {
			if (this.highlightedIndex >= 0) {
				const el = document.getElementById(`result-${this.highlightedIndex}`);
				if (el) el.scrollIntoView({ block: 'nearest' });
			}
		},

		submit() {
			const form = this.$refs.form;

			// Ensure HTML 5 validation is working since we are using Livewire + Alpine.js to submit
			if (!form.checkValidity()) {
				form.reportValidity();
				return;
			}

			// If nothing is selected, always search raw input query
			if (this.highlightedIndex === -1 || !this.resultKeys.length) {
				this.$wire.submit(this.query.trim());
				return;
			}

			// If something is selected (via arrow, hover, or click), submit that term
			const selectedTermId = this.resultKeys[this.highlightedIndex];
			const selected = this.results.find((r) => r.id === selectedTermId);

			if (selected) {
				this.query = selected.name;
				this.$wire.submit(selected.id);
				return;
			}

			this.$wire.submit(this.query.trim());
		},

		// UI hover interaction
		setActiveItem(termId) {
			this.highlightedIndex = this.resultKeys.indexOf(termId);
			this.resultItemActive = termId;
		},

		resultIsActive(termId) {
			return this.resultItemActive === termId;
		},

		// UI expand/collapse interaction
		setExpandedAndShowResults() {
			this.showResults = this.query.trim().length >= this.minQueryLength && this.resultKeys.length > 0;
			this.expanded = this.query.trim().length < this.minQueryLength;
		},
	};
}
